import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useAuth } from '@/features/auth/AuthContext'
import { fetchDashboard } from '@/features/dashboard/api'
import type { DashboardController } from '@/features/dashboard/types'
import {
  OFFLINE_RED,
  batteryKw,
  formatCapacity,
  formatLastSeen,
  gensetKw,
  gridKw,
  irradianceWm2,
  isHiddenForRole,
  prPercent,
  siteSolarKw,
  solarYieldKwhr,
  statusGroup,
  totalCapacityMw,
} from '@/features/dashboard/metrics'
import ErrorState from '@/components/common/ErrorState'
import EmptyState from '@/components/common/EmptyState'
import Skeleton from '@/components/common/Skeleton'

// Simulated skeleton rows shown while the portfolio list is loading. Kept
// well above a typical portfolio size (rather than matching a small fixed
// count like the previous 5) so the shimmer itself doesn't visually imply
// the portfolio is short before real data has loaded.
const SKELETON_ROW_COUNT = 20

type SortKey =
  | 'name'
  | 'alarms'
  | 'capacity'
  | 'lastseen'
  | 'solar'
  | 'grid'
  | 'battery'
  | 'genset'
  | 'yield'
  | 'pr'
  | 'irradiance'

/**
 * Ports dashboard.html -> includes/base_dashboard.html's role router
 * (admin_dashboard.html / staff_dashboard.html / client_dashboard.html,
 * which were three near-duplicate templates) into one role-aware React
 * page, backed by GET /portal/admin/api/v2/dashboard (role-scoped
 * server-side exactly like the three legacy routes were). Column value
 * fallback chains, the offline/online/unknown three-group ordering, the
 * hardcoded-controller-id exclusion, and the capacity/last-seen formatting
 * are all ported verbatim - see features/dashboard/metrics.ts.
 *
 * Not ported: the legacy page's row-click "daily graphs" Highcharts
 * drill-down panel (graphs.js's dashboard_daily_function). Each row instead
 * links straight to that controller's real Analytics page - a genuine
 * navigation to live functionality, not a placeholder, but a UX change
 * from the legacy inline panel worth calling out explicitly rather than
 * silently.
 */
export default function DashboardPage() {
  const { user } = useAuth()
  const [search, setSearch] = useState('')
  const [sortKey, setSortKey] = useState<SortKey | null>(null)
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc')

  const dashboardQuery = useQuery({ queryKey: ['dashboard'], queryFn: fetchDashboard })

  const roleLabel = user?.is_admin || user?.is_super_user ? 'Administrator' : user?.is_staff ? 'Staff' : 'Client'

  const controllersData = dashboardQuery.data?.controllers
  const controllers = useMemo(() => controllersData ?? [], [controllersData])
  const role = dashboardQuery.data?.role ?? 'client'
  const alarmCountsData = dashboardQuery.data?.alarm_counts
  const alarmCounts = useMemo(() => alarmCountsData ?? {}, [alarmCountsData])
  const showAlarms = role === 'admin' || role === 'staff'
  const showSearch = role === 'admin' || role === 'staff'

  const visibleControllers = useMemo(() => {
    let list = controllers.filter((c) => {
      const group = statusGroup(c.online)
      return !isHiddenForRole(c.controller_id, group, role)
    })
    if (search.trim()) {
      const needle = search.trim().toLowerCase()
      list = list.filter((c) => (c.controller_name ?? '').toLowerCase().includes(needle))
    }
    return list
  }, [controllers, role, search])

  const orderedControllers = useMemo(() => {
    if (!sortKey) {
      // Default: offline first, then online, then unknown - matching the
      // legacy template's three-pass render order.
      const order: Record<string, number> = { offline: 0, online: 1, unknown: 2 }
      return [...visibleControllers].sort((a, b) => order[statusGroup(a.online)] - order[statusGroup(b.online)])
    }
    const dir = sortDir === 'asc' ? 1 : -1
    return [...visibleControllers].sort((a, b) => {
      const av = sortValue(a, sortKey, alarmCounts)
      const bv = sortValue(b, sortKey, alarmCounts)
      if (av === undefined && bv === undefined) return 0
      if (av === undefined) return 1
      if (bv === undefined) return -1
      if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * dir
      return String(av).localeCompare(String(bv)) * dir
    })
  }, [visibleControllers, sortKey, sortDir, alarmCounts])

  function toggleSort(key: SortKey) {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortKey(key)
      setSortDir('asc')
    }
  }

  const isLoading = dashboardQuery.isLoading
  if (dashboardQuery.isError) {
    return (
      <ErrorState
        message={dashboardQuery.error instanceof Error ? dashboardQuery.error.message : 'Failed to load dashboard.'}
        onRetry={() => dashboardQuery.refetch()}
      />
    )
  }

  return (
    // Fills AppLayout's <main> exactly (h-full) rather than growing to its
    // natural content height, so the table below can take "all remaining
    // space" as a flex-1 child and be the ONLY thing that scrolls. Before
    // this, the table's own fixed max-height (80vh, sized against the full
    // viewport) didn't account for the header/summary-bar/page padding
    // already consuming part of that viewport, so total page height could
    // exceed <main>'s available space and <main>'s own overflow-y-auto
    // (shared by every page, not changed here) would ALSO start scrolling -
    // two nested scrollbars for what should only ever be one.
    <div className="flex h-full flex-col">
      <div className="shrink-0">
        <h1 className="text-2xl font-bold text-ink">
          Welcome, {user?.firstname || user?.email}
        </h1>
        <span className="mt-2 inline-flex h-[26px] items-center rounded-full bg-nav-hover px-3 text-[12.5px] font-semibold text-nav-text">
          {roleLabel}
        </span>
      </div>

      <div className="mb-4 mt-6 flex shrink-0 flex-wrap items-center gap-4">
        {isLoading ? (
          <Skeleton className="h-4 w-48" />
        ) : (
          <div className="text-sm text-nav-text">
            <b className="font-bold text-ink">{controllers.length}</b> systems ·{' '}
            <b className="font-bold text-ink">{totalCapacityMw(controllers).toFixed(2)} MW</b> total capacity
          </div>
        )}
        {showSearch && (
          <label className="flex h-11 min-w-[240px] flex-1 items-center gap-2.5 rounded-full bg-shell px-[18px] text-nav-muted focus-within:outline focus-within:outline-2 focus-within:outline-brand">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={1.75}
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
              className="h-[18px] w-[18px] shrink-0"
            >
              <circle cx="11" cy="11" r="8" />
              <path d="m21 21-4.3-4.3" />
            </svg>
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search systems"
              aria-label="Search systems"
              className="w-full border-0 bg-transparent text-sm text-ink outline-none placeholder:text-nav-muted"
            />
          </label>
        )}
      </div>

      {!isLoading && controllers.length === 0 ? (
        <EmptyState message="System(s) have not been added to your account yet. Please contact Ariya Team for assistance!" />
      ) : (
        // min-h-0 overrides a flex child's default min-height:auto (content-
        // based) - without it, a flex-1 child still refuses to shrink below
        // its content size, defeating the whole "only this scrolls" goal.
        <div className="min-h-0 flex-1 overflow-x-auto overflow-y-auto rounded-2xl border border-nav-line bg-card">
          <table className="w-full min-w-[1040px] text-[13.5px]">
            <thead className="sticky top-0 z-10 bg-card">
              <tr className="border-b border-nav-line text-left text-xs text-nav-muted">
                <Th label="Name" onClick={() => toggleSort('name')} active={sortKey === 'name'} dir={sortDir} />
                {showAlarms && (
                  <Th label="Alarms" onClick={() => toggleSort('alarms')} active={sortKey === 'alarms'} dir={sortDir} />
                )}
                <Th label="Capacity (kW)" onClick={() => toggleSort('capacity')} active={sortKey === 'capacity'} dir={sortDir} />
                <Th label="Last data" onClick={() => toggleSort('lastseen')} active={sortKey === 'lastseen'} dir={sortDir} />
                <Th label="Site solar (kW)" onClick={() => toggleSort('solar')} active={sortKey === 'solar'} dir={sortDir} />
                <Th label="Grid (kW)" onClick={() => toggleSort('grid')} active={sortKey === 'grid'} dir={sortDir} />
                <Th label="Battery (kW)" onClick={() => toggleSort('battery')} active={sortKey === 'battery'} dir={sortDir} />
                <Th label="Genset (kW)" onClick={() => toggleSort('genset')} active={sortKey === 'genset'} dir={sortDir} />
                <Th label="Solar yield (kWh)" onClick={() => toggleSort('yield')} active={sortKey === 'yield'} dir={sortDir} />
                <Th label="PR (%)" onClick={() => toggleSort('pr')} active={sortKey === 'pr'} dir={sortDir} />
                <Th label="Irradiance (W/m²)" onClick={() => toggleSort('irradiance')} active={sortKey === 'irradiance'} dir={sortDir} />
              </tr>
            </thead>
            <tbody>
              {isLoading &&
                Array.from({ length: SKELETON_ROW_COUNT }).map((_, i) => (
                  <tr key={i} className="border-b border-nav-line last:border-0">
                    {Array.from({ length: showAlarms ? 11 : 10 }).map((__, j) => (
                      <td key={j} className="px-4 py-3.5">
                        <Skeleton className="h-3.5 w-full max-w-[6rem]" />
                      </td>
                    ))}
                  </tr>
                ))}
              {!isLoading &&
                orderedControllers.map((c) => {
                const group = statusGroup(c.online)
                const isOfflineRed = group === 'offline' || (group === 'unknown' && role === 'client')
                const statusClass = isOfflineRed
                  ? ''
                  : group === 'online'
                    ? 'border-brand text-brand'
                    : 'border-text-secondary text-text-secondary'
                const dangerStyle = isOfflineRed ? { color: OFFLINE_RED, borderColor: OFFLINE_RED } : undefined
                const alarm = alarmCounts[c.controller_id]
                const lastSeen = formatLastSeen(c.lastseen)
                return (
                  <tr key={c.controller_id} className="border-b border-nav-line last:border-0 hover:bg-row-hover">
                    <td className="px-4 py-3.5">
                      <Link
                        to={`/site-details?controller=${c.controller_id}`}
                        style={dangerStyle}
                        className={`block h-[34px] w-full min-w-[230px] max-w-[250px] truncate rounded-[10px] border-[1.5px] px-3.5 text-left font-semibold leading-[31px] hover:bg-card hover:no-underline ${statusClass}`}
                      >
                        {c.controller_name || c.controller_id}
                      </Link>
                    </td>
                    {showAlarms && (
                      <td className="px-4 py-3.5">
                        <Link
                          to={`/settings-alarms?controller_id=${c.controller_id}`}
                          style={
                            alarm && (alarm.current_count > 0 || alarm.has_major_alarm)
                              ? { color: OFFLINE_RED, backgroundColor: `${OFFLINE_RED}1a` }
                              : undefined
                          }
                          className={
                            alarm && (alarm.current_count > 0 || alarm.has_major_alarm)
                              ? 'inline-flex h-6 items-center gap-1.5 rounded-full px-2.5 text-xs font-semibold hover:no-underline'
                              : 'text-nav-muted'
                          }
                        >
                          {alarm ? alarm.current_count : 0}
                          {alarm?.has_major_alarm && <span>Major</span>}
                        </Link>
                      </td>
                    )}
                    <td className="px-4 py-3.5 text-ink">{formatCapacity(c.capacity)}</td>
                    <td className="px-4 py-3.5 leading-[1.3] text-ink">
                      {lastSeen ? (
                        <>
                          {lastSeen.date}
                          <span className="block text-xs text-nav-muted">{lastSeen.time}</span>
                        </>
                      ) : (
                        <Dash />
                      )}
                    </td>
                    <MetricCell value={siteSolarKw(c.registers)} color="#ffc107" />
                    <MetricCell value={gridKw(c.registers)} color="#B20600" />
                    <MetricCell value={batteryKw(c.registers)} color="#2F8F9D" />
                    <MetricCell value={gensetKw(c.registers)} color="var(--genset-color)" />
                    <MetricCell value={solarYieldKwhr(c.registers)} color="#ffc107" />
                    <MetricCell value={prPercent(c.registers)} color="#8946A6" />
                    <MetricCell value={irradianceWm2(c.registers)} color="#F10086" />
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

function sortValue(
  c: DashboardController,
  key: SortKey,
  alarmCounts: Record<string, { current_count: number }>,
): number | string | undefined {
  switch (key) {
    case 'name':
      return c.controller_name ?? c.controller_id
    case 'alarms':
      return alarmCounts[c.controller_id]?.current_count ?? 0
    case 'capacity':
      return c.capacity != null ? Number(c.capacity) : undefined
    case 'lastseen':
      return c.lastseen ?? undefined
    case 'solar':
      return siteSolarKw(c.registers)
    case 'grid':
      return gridKw(c.registers)
    case 'battery':
      return batteryKw(c.registers)
    case 'genset':
      return gensetKw(c.registers)
    case 'yield':
      return solarYieldKwhr(c.registers)
    case 'pr':
      return prPercent(c.registers)
    case 'irradiance':
      return irradianceWm2(c.registers)
    default:
      return undefined
  }
}

function Th({ label, onClick, active, dir }: { label: string; onClick: () => void; active: boolean; dir: 'asc' | 'desc' }) {
  return (
    <th
      onClick={onClick}
      className={`cursor-pointer select-none whitespace-nowrap px-4 py-3.5 font-semibold hover:text-brand ${active ? 'text-brand' : ''}`}
    >
      {label}
      {active && <span className="ml-1">{dir === 'asc' ? '▲' : '▼'}</span>}
    </th>
  )
}

function MetricCell({ value, color }: { value: number | undefined; color: string }) {
  return (
    <td className="px-4 py-3.5" style={{ color }}>
      {value !== undefined ? value : <Dash />}
    </td>
  )
}

function Dash() {
  return <span className="text-text-tertiary">—</span>
}
